/**
 * AVA-475 — error vectors (per-axis deviation arrows on as-built markers).
 *
 * Turns a completed verification point into three screen-space arrows showing
 * the design→as-built deviation per axis: direction carries the sign, colour
 * carries the tolerance band, the number carries the magnitude in millimetres.
 * Arrow LENGTH is deliberately uniform and means nothing — a length-coded arrow
 * invites reading two points against each other, and these are per-axis
 * conformance values, not a field of vectors.
 *
 * NOTHING HERE IS STORED. The deltas, tolerance and verdict all arrive already
 * derived from `VERIFICATION/GET_POINTS_WITH_RESULTS`; this module only decides
 * which axes are worth drawing, how to colour them, and where they point on
 * screen. Same single-source-of-truth rule the rest of the feature follows
 * (utils/pointMath.js).
 *
 * DEPENDENCY-FREE ON PURPOSE. No Vue, no store, no viewer globals, and no
 * imports from sibling utils — the `.claude/scratch/errorVectors.assert.js`
 * harness loads this file's real bytes and evaluates them standalone, so every
 * import added here becomes a file that harness has to concatenate first. The
 * viewer is passed in; even `THREE.Vector3` arrives as an injected factory.
 *
 * UNITS: deltas and tolerances are MILLIMETRES (already converted by
 * `computeDeltas`). World positions and the epsilon step are VIEWER units —
 * metres on some facilities, millimetres on others (utils/modelUnits.js). The
 * only real METRES here are the two epsilon clamp bounds, and they are
 * converted at the point of use from a scale the CALLER supplies. Nothing in
 * this file resolves units; that would need an import, and the header above is
 * why there are none.
 */

/** Severity bands. Frozen so a typo'd band is a crash, not a silent no-colour. */
export const SEVERITY = Object.freeze({
  PASS: 'pass',
  WARN: 'warn',
  FAIL: 'fail',
});

/**
 * Arrow colours, by severity.
 *
 * DELIBERATELY NOT the feature's `$vf-*` tokens. Those are WCAG-darkened for
 * dark text on a LIGHT panel; drawn on the viewer's dark canvas and over a
 * point cloud they read as muddy near-black. These are the bright on-canvas
 * equivalents of the same three bands.
 *
 * Colour means SEVERITY, never axis identity. `markerPalette.js` already
 * reserves green/red for verdict semantics across this feature, and the axis
 * triad (utils/axisTriad.js) owns CAD X=red/Y=green/Z=blue for identity — so an
 * axis-coloured arrow would read as a verdict to anyone who has seen a marker,
 * and a severity-coloured one cannot be mistaken for an axis. Which axis an
 * arrow belongs to is conveyed by where it POINTS; the labelled world triad in
 * the corner is its legend.
 */
export const ERROR_VECTOR_COLORS = Object.freeze({
  [SEVERITY.PASS]: '#22c55e',
  [SEVERITY.WARN]: '#f59e0b',
  [SEVERITY.FAIL]: '#ef4444',
});

/**
 * The three axes, in report order. Intentionally a local copy of the same list
 * `axisLock.js` exports as `LOCKABLE_AXES` rather than an import of it: this
 * module stays dependency-free (see file header), and `LOCKABLE_AXES` is scoped
 * to "axes that can carry a lock", which is a different question that only
 * happens to have the same answer.
 */
const VECTOR_AXES = Object.freeze(['x', 'y', 'z']);

/** Axis key → the key `computeDeltas` writes that axis' signed delta under. */
const AXIS_DELTA_KEY = Object.freeze({ x: 'dX', y: 'dY', z: 'dZ' });

/** Warn band ceiling, as a multiple of the axis tolerance. Above it: fail. */
const WARN_TOLERANCE_MULTIPLE = 2;

/**
 * Float-boundary epsilon in MILLIMETRES — a DELIBERATE COPY of
 * `FLOAT_EPSILON_MM` in pointMath.js, not an oversight.
 *
 * This module is dependency-free on purpose (see the file header): the scratch
 * assert harness loads these bytes standalone, so an import here becomes a file
 * that harness must concatenate first. axisLock.js keeps its own copy for the
 * same class of reason. The value is a nanometre and will not change; if it
 * ever does, both copies move together — that is what the lockstep note on
 * `classifyDeviation` is for.
 */
const FLOAT_EPSILON_MM = 1e-6;

/**
 * Decimals on an arrow's magnitude — a DELIBERATE COPY of `DEVIATION_DECIMALS`
 * in pointMath.js, for the same dependency-free reason as `FLOAT_EPSILON_MM`
 * above, and it must move with it.
 *
 * THE ARROW AND THE PANEL STATE ONE MEASUREMENT. They sat at different
 * precisions: the results panel rendered `+4.9` while the arrow on the marker
 * beside it rounded to `5`. Two numbers for one deviation is a discrepancy the
 * surveyor has to resolve, and neither surface tells them which is the rounded
 * one.
 */
const DEVIATION_DECIMALS = 1;

/** Magnitude steps per millimetre implied by `DEVIATION_DECIMALS`. */
const MAGNITUDE_STEPS_PER_MM = 10 ** DEVIATION_DECIMALS;

/**
 * An arrow's magnitude as it is written: unsigned, at label precision.
 *
 * UNSIGNED because the arrow states its sign by pointing — a `+` beside it is a
 * second answer to a question the geometry has already settled. That is the one
 * respect in which this differs from `formatDelta`; the DIGITS are identical, and
 * `formatDeviationMagnitude` in pointMath.js is its named counterpart there.
 *
 * Exported so the renderer shares it rather than reformatting the same number a
 * second way (MarkerErrorVectors.vue). `ERROR_VECTOR_COLORS` is exported and
 * consumed the same way.
 *
 * RETURNS AN EMPTY STRING, NOT AN EM DASH, and that is not an oversight to be
 * tidied into line with `formatDeviationMagnitude`. This string is painted onto
 * a canvas beside an arrow: a dash there is a mark the reader has to interpret,
 * where nothing at all is self-explanatory. `markerErrorVectors.assert.js`
 * pins it ("a label with no usable value draws nothing rather than
 * 'undefined'").
 *
 * @param {number} mm Millimetres; sign ignored.
 * @returns {string} '' when there is no number to write.
 */
export function formatMagnitudeMm(mm) {
  return Number.isFinite(mm) ? Math.abs(mm).toFixed(DEVIATION_DECIMALS) : '';
}

/**
 * An axis is degenerate when its projected step is shorter than this fraction
 * of the longest of the three. RELATIVE on purpose — an absolute pixel
 * threshold would answer differently at different zooms, because the projected
 * step of a fixed world epsilon shrinks as the camera pulls back. This asks the
 * zoom-independent question: "does this axis have a direction on screen at all,
 * compared to the axes that do?"
 */
export const DEGENERATE_DIRECTION_RATIO = 0.15;

/**
 * Below this the longest projected step is treated as no step at all. Guards
 * the normalisation divide and the degenerate ratio against a camera state
 * where all three axes collapse to a point.
 */
const MIN_RESOLVABLE_STEP_PX = 1e-6;

/**
 * Probe step used when nothing better is available, in METRES. The `_M` suffix
 * is the whole point: it becomes viewer units only after being scaled.
 */
export const DEFAULT_EPSILON_M = 0.05;
/** Unitless — a fraction of the geometry, so it survives any facility as is. */
const EPSILON_EXTENT_FRACTION = 0.05;
/** Both METRES, converted at the point of use. See `epsilonForExtent`. */
const EPSILON_MIN_M = 0.01;
const EPSILON_MAX_M = 0.5;

/**
 * Tolerance band for one axis, from the RAW millimetre deviation.
 *
 * Boundary INCLUSIVE, matching `evaluatePoint` exactly: |d| <= tol passes. The
 * two must agree or an arrow would show green on an axis the report calls FAIL.
 * That agreement now includes the float epsilon — `evaluatePoint` compares
 * `|d| - tol <= FLOAT_EPSILON_MM`, so this must too, or the two verdicts
 * separate again on exactly the boundary values a 4-decimal entry field invites
 * a surveyor to type.
 *
 * The epsilon is applied to the WARN ceiling as well. It is an absorber of
 * binary representation error, not a relaxation of a tolerance, and `tolMm * 2`
 * is itself a float multiply that can land a hair either side. Applying it to
 * one comparison and not the neighbouring one, three lines apart, is the kind
 * of asymmetry a later reader "tidies" — and tidying it the wrong way puts the
 * conformance bug back. Nothing about a verdict changes: both sides of that
 * ceiling are already non-passing, so it moves an arrow between amber and red
 * and no further.
 *
 * Absent or nonsensical tolerance yields `null` — never a guessed band. An
 * arrow whose colour was invented is worse than no arrow, because nothing about
 * it looks invented.
 *
 * @param {number} absMmRaw Unrounded |deviation| in millimetres.
 * @param {number} tolMm Axis tolerance in millimetres.
 * @returns {('pass'|'warn'|'fail'|null)}
 */
export function classifyDeviation(absMmRaw, tolMm) {
  if (!Number.isFinite(absMmRaw) || !Number.isFinite(tolMm) || tolMm < 0) return null;
  if (absMmRaw - tolMm <= FLOAT_EPSILON_MM) return SEVERITY.PASS;
  if (absMmRaw - tolMm * WARN_TOLERANCE_MULTIPLE <= FLOAT_EPSILON_MM) return SEVERITY.WARN;
  return SEVERITY.FAIL;
}

/**
 * The arrows a point should draw — one per axis worth drawing, in report order.
 *
 * Four reasons an axis draws nothing:
 *   1. The point is incomplete. No as-built, no deviation, no arrow.
 *   2. The axis is LOCKED. It was asserted from design, so its delta is 0 by
 *      construction and an arrow would illustrate an assumption as a
 *      measurement (axisLock.js RULE 1).
 *   3. Its tolerance is missing, so no band can be honestly assigned.
 *   4. It rounds to 0.0 mm AND passes — nothing to show at label precision.
 *
 * Severity classifies on the RAW delta while the label shows the ROUNDED one,
 * so a 3.04 mm deviation against ±3 reads amber and prints "3.0": the colour
 * answers "is this in tolerance", the number answers "by how much", and the
 * colour is the one that must not be rounded into a lie.
 *
 * @param {object} point One entry of `VERIFICATION/GET_POINTS_WITH_RESULTS`.
 * @returns {Array<{axis, deltaMm, valueMm, label, severity}>} deltaMm signed and
 *   raw; `valueMm` unsigned at label precision; `label` is `valueMm` written.
 */
export function buildVectorModel(point) {
  if (!point || !point.isComplete || !point.deltas) return [];

  const locked = new Set(point.lockedAxes || []);
  const vectors = [];

  for (const axis of VECTOR_AXES) {
    if (locked.has(axis)) continue;

    const deltaMm = point.deltas[AXIS_DELTA_KEY[axis]];
    if (!Number.isFinite(deltaMm)) continue;

    const absMm = Math.abs(deltaMm);
    const severity = classifyDeviation(absMm, point.tolerance?.[axis]);
    if (!severity) continue;

    // Rounded-to-zero axes are dropped only when they PASS. An axis can round
    // to 0 mm and still be out of tolerance once the surveyor tightens the
    // tolerance below a twentieth of a millimetre, and silently drawing nothing
    // on a failing axis is the same fabricated-completeness failure axisLock.js
    // RULE 1 exists to prevent: the reader sees two arrows, no warning, and no
    // reason to suspect a third axis was ever checked.
    //
    // AT LABEL PRECISION, which is now a tenth of a millimetre rather than a
    // whole one — so the band that draws nothing narrowed by a factor of ten and
    // sub-millimetre deviations that used to vanish now draw an arrow. That is
    // the intended consequence of stating the same number the panel states.
    const valueMm = Math.round(absMm * MAGNITUDE_STEPS_PER_MM) / MAGNITUDE_STEPS_PER_MM;
    if (valueMm === 0 && severity === SEVERITY.PASS) continue;

    vectors.push({
      axis,
      deltaMm,
      valueMm,
      // "<0.1" rather than "0.0" for the sub-tenth out-of-tolerance case above —
      // an amber arrow labelled "0.0" reads as a rendering bug.
      //
      // Formatted from the ROUNDED value, not from `absMm`: one rounding, so the
      // number in the label and the number in `valueMm` cannot part company.
      label: valueMm === 0 ? '<0.1' : formatMagnitudeMm(valueMm),
      severity,
    });
  }

  return vectors;
}

/** Every axis without a screen direction. Fresh objects — callers keep these. */
function allDegenerate() {
  return {
    x: { dx: 0, dy: 0, degenerate: true },
    y: { dx: 0, dy: 0, degenerate: true },
    z: { dx: 0, dy: 0, degenerate: true },
  };
}

function isFinitePoint(point) {
  return !!point
    && Number.isFinite(point.x)
    && Number.isFinite(point.y)
    && Number.isFinite(point.z);
}

/**
 * Unit screen direction of each WORLD basis axis at `worldPos`.
 *
 * Projects `worldPos` and `worldPos + epsilon·axis` and takes the difference,
 * which is the only way to get a screen direction out of a viewer that exposes
 * projection but not its matrices.
 *
 * ALL THREE AXES ARE ALWAYS COMPUTED, before any caller-side filtering, and the
 * result is UNSIGNED (the +axis direction). Both matter:
 *
 *   - Degeneracy is relative to the longest of the three, so the three must be
 *     measured together. Compute only the surviving axes and a lone
 *     out-of-plane axis in an orthographic view becomes its own maximum and
 *     reports itself as a perfectly good direction — an arrow pointing along
 *     an axis the view physically cannot show.
 *   - Signs belong to the deltas, not the basis. The caller flips the returned
 *     direction by its delta's sign, so this function stays a property of the
 *     camera alone and can be computed once per point regardless of how many
 *     arrows end up drawn.
 *
 * Any non-finite projection anywhere makes ALL THREE degenerate rather than
 * just the offending axis: if the camera cannot project one corner of this
 * probe, the other two directions are not trustworthy either.
 *
 * @param {object} viewer Forge viewer (needs `worldToClient`).
 * @param {{x,y,z}} worldPos World position in VIEWER units — it is handed
 *   straight to `worldToClient`, so it must be in the space the viewer projects.
 * @param {number} epsilon Probe step, also VIEWER units. `epsilonForExtent`
 *   converts its metre-denominated clamp bounds before returning it.
 * @param {function} makeVector (x,y,z) => THREE.Vector3. Injected because
 *   `worldToClient` clones and matrix-multiplies its argument, so a plain
 *   object will not do — and reaching for `window.THREE` here would cost this
 *   module its standalone testability for no gain.
 * @returns {{x:{dx,dy,degenerate}, y:{…}, z:{…}}} dx/dy are unit-length, or
 *   0/0 when degenerate.
 */
export function computeAxisScreenVectors(viewer, worldPos, epsilon, makeVector) {
  if (typeof viewer?.worldToClient !== 'function'
    || typeof makeVector !== 'function'
    || !isFinitePoint(worldPos)
    || !Number.isFinite(epsilon)
    || epsilon <= 0) {
    return allDegenerate();
  }

  const project = (x, y, z) => {
    const client = viewer.worldToClient(makeVector(x, y, z));
    if (!client || !Number.isFinite(client.x) || !Number.isFinite(client.y)) return null;
    return client;
  };

  const origin = project(worldPos.x, worldPos.y, worldPos.z);
  if (!origin) return allDegenerate();

  const steps = {};
  let longest = 0;

  for (const axis of VECTOR_AXES) {
    const stepped = project(
      worldPos.x + (axis === 'x' ? epsilon : 0),
      worldPos.y + (axis === 'y' ? epsilon : 0),
      worldPos.z + (axis === 'z' ? epsilon : 0),
    );
    if (!stepped) return allDegenerate();

    const dx = stepped.x - origin.x;
    const dy = stepped.y - origin.y;
    const length = Math.hypot(dx, dy);
    if (!Number.isFinite(length)) return allDegenerate();

    steps[axis] = { dx, dy, length };
    if (length > longest) longest = length;
  }

  if (longest <= MIN_RESOLVABLE_STEP_PX) return allDegenerate();

  const directions = {};
  for (const axis of VECTOR_AXES) {
    const { dx, dy, length } = steps[axis];
    directions[axis] = length < longest * DEGENERATE_DIRECTION_RATIO
      ? { dx: 0, dy: 0, degenerate: true }
      : { dx: dx / length, dy: dy / length, degenerate: false };
  }
  return directions;
}

/**
 * Probe step for `computeAxisScreenVectors`, sized to the scene.
 *
 * Too small and the difference between two projections is float noise; too
 * large and perspective skews the direction away from the axis at the point.
 * A fraction of the verified geometry's largest extent sits between the two at
 * any scale — and survives a facility whose model units are not metres, where
 * a hard-coded 0.05 would be either noise or a step across the whole support.
 *
 * THE CLAMP BOUNDS ARE METRES AND THE EXTENT IS NOT, so the bounds are scaled
 * before they are applied. Unscaled on a millimetre model they read as 0.01 mm
 * and 0.5 mm, which pins every probe step to half a millimetre — float noise
 * against a 1.4 m support — and the arrows lose their direction. The fraction
 * itself needs no conversion: it is a ratio of the geometry to itself.
 *
 * `viewerUnitsPerMetre` comes from the caller's `unitTransform` (`1 / siteScale`).
 *
 * IT FALLS BACK TO 1 WHEN ABSENT, AND THAT IS SAFE ONLY BECAUSE OF AN INVARIANT
 * ELSEWHERE — say so out loud, because a silent default is the exact shape of
 * the bug this whole change exists to remove, and `computeDeltas` refuses to
 * have one three files away. The invariant: a step is consumed only through
 * `buildVectorModel`, which returns NO vectors for a point whose `deltas` are
 * null, and deltas are null for every point while the transform is unresolved.
 * So a step computed without a scale is never drawn with. Returning null here
 * instead would be worse than the fallback — this function has no null contract,
 * and one would propagate into the projection maths as NaN.
 *
 * IF THAT INVARIANT EVER CHANGES — if anything starts drawing arrows for a
 * point with null deltas — this fallback becomes a live defect: on a millimetre
 * facility the clamp would pin every probe to half a millimetre against a
 * ~1400-unit support, which is float noise, and every axis would report itself
 * degenerate. Guard the CALLER at that point, the way `drawAxisTriad` and
 * `measureMmPerPx` are guarded.
 *
 * @param {number} maxExtent Largest verified-bbox extent, in VIEWER units.
 * @param {number|null} [viewerUnitsPerMetre] Viewer units in one real metre.
 * @returns {number} Clamped step in VIEWER units.
 */
export function epsilonForExtent(maxExtent, viewerUnitsPerMetre = null) {
  const perMetre = Number.isFinite(viewerUnitsPerMetre) && viewerUnitsPerMetre > 0
    ? viewerUnitsPerMetre
    : 1;
  if (!Number.isFinite(maxExtent) || maxExtent <= 0) return DEFAULT_EPSILON_M * perMetre;
  const scaled = maxExtent * EPSILON_EXTENT_FRACTION;
  return Math.min(
    Math.max(scaled, EPSILON_MIN_M * perMetre),
    EPSILON_MAX_M * perMetre,
  );
}
